import {Box, Button, IconButton, TextField, Tooltip, Typography} from "@mui/material";
import {Add as AddIcon, Close as CloseIcon} from "@mui/icons-material";
import {useEffect, useRef, useState} from "react";
import {borders, colors} from "../ui/colors";

const labelSx = {fontSize: "13px", fontWeight: 500, color: colors.textSecondary, mb: 0.5};

const fieldSx = {
    "& .MuiInputBase-input": {fontSize: "13px", fontFamily: "monospace"},
};

const objectToRows = (obj) => {
    const entries = Object.entries(obj || {});
    if (!entries.length) return [{key: "", value: ""}];
    return entries.map(([key, value]) => ({key, value: value ?? ""}));
};

/**
 * Inline key/value editor for HTTP headers. Local state holds the rows being
 * edited (including empty rows the user is mid-typing) so the parent only sees
 * the cleaned `{ key: value }` map. This decouples display from commit:
 *  - "+ הוסף header" adds an empty row to local state without touching the
 *    parent — the parent's `value` only includes filled keys.
 *  - Inputs stay focused across keystrokes because the row identity is local.
 *
 * Parent re-seeds (`value` prop changes from outside) re-initialize local state
 * via the `seed` ref so opening the wizard on a different automation works.
 */
const HeadersEditor = ({value = {}, onChange}) => {
    const [rows, setRows] = useState(() => objectToRows(value));
    const lastSeedRef = useRef(value);

    // If the parent passes in a structurally different value (e.g. user opened
    // a different automation), re-seed our local rows. We use a JSON compare to
    // avoid resetting on the round-tripped values we just emitted ourselves.
    useEffect(() => {
        const incoming = JSON.stringify(value || {});
        const lastEmitted = JSON.stringify(toObject(rows));
        const lastSeed = JSON.stringify(lastSeedRef.current || {});
        if (incoming !== lastEmitted && incoming !== lastSeed) {
            setRows(objectToRows(value));
            lastSeedRef.current = value;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const commit = (nextRows) => {
        setRows(nextRows);
        onChange(toObject(nextRows));
    };

    const updateRow = (i, patch) => {
        commit(rows.map((r, idx) => (idx === i ? {...r, ...patch} : r)));
    };

    const addRow = () => {
        commit([...rows, {key: "", value: ""}]);
    };

    const removeRow = (i) => {
        commit(rows.filter((_, idx) => idx !== i));
    };

    return (
        <Box>
            <Box sx={{display: "flex", alignItems: "center", gap: 0.5, mb: 0.5}}>
                <Typography sx={labelSx}>Headers</Typography>
                <Tooltip
                    title="לדוגמה: Authorization: Bearer xxx. נשלח עם כל בקשה."
                    placement="top"
                    arrow
                >
                    <Box
                        component="span"
                        sx={{
                            fontSize: "11px",
                            color: colors.textMuted,
                            cursor: "help",
                            textDecoration: "underline dotted",
                        }}
                    >
                        ?
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{display: "flex", flexDirection: "column", gap: 0.75}}>
                {rows.map((row, i) => (
                    <Box key={i} sx={{display: "flex", gap: 0.75, alignItems: "center"}}>
                        <TextField
                            size="small"
                            placeholder="Authorization"
                            value={row.key}
                            onChange={e => updateRow(i, {key: e.target.value})}
                            sx={{...fieldSx, flex: "0 0 38%"}}
                        />
                        <TextField
                            size="small"
                            placeholder="Bearer xxx"
                            value={row.value}
                            onChange={e => updateRow(i, {value: e.target.value})}
                            sx={{...fieldSx, flex: 1}}
                        />
                        <IconButton
                            size="small"
                            onClick={() => removeRow(i)}
                            sx={{color: colors.textMuted}}
                            aria-label="הסר header"
                        >
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </Box>
                ))}
            </Box>
            <Button
                size="small"
                startIcon={<AddIcon/>}
                onClick={addRow}
                sx={{mt: 1, color: colors.primary}}
            >
                הוסף header
            </Button>
            <Box sx={{mt: 0.5, fontSize: "11px", color: colors.textMuted}}>
                * Content-Type נקבע אוטומטית (application/json) ולא ניתן לדריסה.
            </Box>
            <Box sx={{height: 0, border: "0", borderTop: borders.default, mt: 1.5}}/>
        </Box>
    );
};

const toObject = (rows) => {
    const obj = {};
    rows.forEach(r => {
        if (r.key && r.key.trim()) obj[r.key.trim()] = r.value ?? "";
    });
    return obj;
};

export default HeadersEditor;
